import React, { useContext, useEffect, useState } from 'react';
import { CountryCode } from '@dayetopia/types';
import { PrismicRichText } from '@prismicio/react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { BREAKPOINT } from '@constants';
import { PageSlice, SliceHeroFullWidthImageDetails, ProductCode } from '@contracts';
import { getMinPrice } from '@utils/boxPrices';
import { formatPriceBasedOnCountry } from '@utils/price';
import Colors from '../Colors';
import LinkButton from '../LinkButton';
import { Gradient2Style } from '../SharedStyles';
import useDesktop from '../useDesktop';
import { TEXT_STYLES, TextVariant } from './Typography';

interface Props {
  slice: PageSlice;
}

const HeroWithFullWidthImage: React.FC<Props> = ({ slice }) => {
  const { t } = useTranslation();
  const {
    state: { country, currency }
  } = useContext(Context);
  const sliceDetails = slice.primary as SliceHeroFullWidthImageDetails;
  const isMobile = !useDesktop(BREAKPOINT.mobileM);
  const [startingPrice, setStartingPrice] = useState<string>('');

  const darkButton = sliceDetails?.dark_button;
  const darkText = sliceDetails?.dark_text;
  const alignTopMobile = isMobile && sliceDetails?.align_top_mobile;
  const isFsaHsaEligible = startingPrice !== '-1' && country === CountryCode.US && sliceDetails.fsa_hsa_eligible;

  useEffect(() => {
    const fetchStartingPrice = async () => {
      const startingPriceData = await getMinPrice(sliceDetails?.product_code as ProductCode, currency, country);
      setStartingPrice(startingPriceData?.price && startingPriceData?.price > 0 ? startingPriceData.price.toFixed(2) : '-1');
    };
    fetchStartingPrice();
  }, [sliceDetails?.product_code, currency, country]);

  return (
    <Wrapper>
      <BackgroundImage src={(isMobile ? sliceDetails.background_image_mobile.url : sliceDetails.background_image.url) ?? ''} />
      <ContentBlock $darkText={darkText} $alignTopMobile={alignTopMobile}>
        {sliceDetails.heading.richText.length > 0 && (
          <Heading>
            <PrismicRichText field={sliceDetails.heading.richText} />
          </Heading>
        )}
        {sliceDetails.subheading.richText.length > 0 && (
          <Subheading>
            <PrismicRichText field={sliceDetails.subheading.richText} />
          </Subheading>
        )}
        <PricingBlock>
          {sliceDetails.show_starting_at && startingPrice !== '-1' && (
            <StartingFrom>
              {t('components.starting-from')}: {formatPriceBasedOnCountry(startingPrice, currency, country)}
            </StartingFrom>
          )}
          {isFsaHsaEligible && <FSAHSANotice>{t('components.fsa-hsa-notice')}</FSAHSANotice>}
        </PricingBlock>
        {sliceDetails.button_url && (
          <LinkButton
            isInline
            $buttonTextColor={darkButton ? Colors.cream : undefined}
            backgroundColor={darkButton ? Colors.navyBlue : undefined}
            to={sliceDetails?.button_url}
          >
            {sliceDetails.button_text_key}
          </LinkButton>
        )}
      </ContentBlock>
    </Wrapper>
  );
};

export default HeroWithFullWidthImage;

const Wrapper = styled.div`
  width: 100%;
  height: 720px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${BREAKPOINT.mobileM}px) {
    height: 600px;
  }
`;

const BackgroundImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const ContentBlock = styled.div<{ $darkText?: boolean; $alignTopMobile: boolean }>`
  width: 720px;
  max-width: 90vw;
  margin: ${(props) => (props.$alignTopMobile ? '32px auto' : 'auto auto')};
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 24px;
  text-align: center;
  position: relative;
  z-index: 2;
  color: ${(props) => (props.$darkText ? Colors.navyBlue : Colors.cream)};
`;

const Heading = styled.div`
  & > * {
    ${TEXT_STYLES[TextVariant.Heading1]};
    color: inherit;

    @media (max-width: ${BREAKPOINT.tablet}px) {
      ${TEXT_STYLES[TextVariant.Heading3]};
    }

    @media (max-width: ${BREAKPOINT.mobileM}px) {
      ${TEXT_STYLES[TextVariant.Heading5]};
    }

    & > span.italic {
      font-style: italic;
    }
    & span.gradient2 {
      ${Gradient2Style}
    }
  }
`;

const Subheading = styled.div`
  width: 600px;
  max-width: 80%;

  & > * {
    ${TEXT_STYLES[TextVariant.BodyRegularLG]};
    color: inherit;

    @media (max-width: ${BREAKPOINT.tablet}px) {
      ${TEXT_STYLES[TextVariant.BodyRegularMD]};
    }

    & > span.bold {
      font-weight: 700;
    }
  }
`;

const PricingBlock = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 2px;
`;

const StartingFrom = styled.p`
  ${TEXT_STYLES[TextVariant.UpperText3]}
  color: inherit;
`;

const FSAHSANotice = styled.p`
  ${TEXT_STYLES[TextVariant.UpperText3]}
  color: inherit;
`;
